import { useState } from 'react';
import { useAppStore } from '../hooks/useAppStore';
import { useToast } from '../hooks/useToast';
import { copyToClipboard } from '../api/mock';
import Toast from '../components/Toast';
import { Copy, Share2, ArrowRight, UserPlus, Clock, Shield } from 'lucide-react';

export default function Invite() {
  const { user } = useAppStore();
  const toast = useToast();
  const [tab, setTab] = useState('share');
  const [inputId, setInputId] = useState('');
  const [inputOtp, setInputOtp] = useState('');

  const handleCopy = async () => {
    await copyToClipboard(`${user.profileId} | OTP: ${user.otp}`);
    toast.show('Copied!');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title:'Burner Codes', text:`Link with me on Burner Codes!\nProfile: ${user.profileId}\nOTP: ${user.otp}` }); } catch {}
    } else { handleCopy(); }
  };

  return (
    <div className="px-4 py-5 pb-8 animate-fade-in">
      <h1 className="text-xl font-bold text-gray-100 mb-1">Partner Linking</h1>
      <p className="text-sm text-gray-500 mb-6">Share your code or enter someone else's to connect.</p>

      {/* Tab toggle */}
      <div className="flex gap-2 mb-6">
        {['share','join'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${tab===t ? 'bg-burner-500/15 text-burner-400 border border-burner-500/25' : 'bg-zinc-800/40 text-gray-500 border border-transparent'}`}>
            {t==='share' ? 'Share My Code' : 'Enter a Code'}
          </button>
        ))}
      </div>

      {tab === 'share' ? (
        <div className="space-y-4">
          <div className="glass p-6 text-center">
            <p className="section-label mb-2">Your Profile ID</p>
            <p className="text-2xl font-mono font-bold text-burner-400 tracking-wider mb-4">{user.profileId}</p>
            <div className="glass-sm inline-flex items-center gap-2 px-4 py-2">
              <Clock size={14} className="text-gray-500" />
              <span className="section-label">OTP</span>
              <span className="font-mono text-sm text-gray-100 font-semibold tracking-widest">{user.otp}</span>
              <span className="text-[10px] text-zinc-500">expires {user.otpExpiresIn}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={handleCopy} className="btn-gradient flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white"><Copy size={16}/>Copy</button>
            <button onClick={handleShare} className="btn-ghost flex-1"><Share2 size={16}/>Share</button>
          </div>

          <div className="glass-sm p-4 flex items-start gap-3">
            <Shield size={16} className="text-burner-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-400 leading-relaxed">The OTP is single-use and expires. Your partner enters both your Profile ID and this OTP to link. SIGPRINTs are generated fresh for this specific pairing.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="glass p-6 space-y-4">
            <div>
              <label className="section-label block mb-2">Partner's Profile ID</label>
              <input type="text" value={inputId} onChange={e=>setInputId(e.target.value)} placeholder="BC-XXXX-XXXX"
                className="w-full rounded-xl bg-zinc-800/60 border border-white/[0.06] px-4 py-3 text-sm font-mono text-gray-100 placeholder-gray-600 focus:border-burner-500/40 focus:outline-none transition-colors tracking-wider" />
            </div>
            <div>
              <label className="section-label block mb-2">One-Time Passcode</label>
              <input type="text" value={inputOtp} onChange={e=>setInputOtp(e.target.value)} placeholder="000000" maxLength={6}
                className="w-full rounded-xl bg-zinc-800/60 border border-white/[0.06] px-4 py-3 text-sm font-mono text-gray-100 placeholder-gray-600 focus:border-burner-500/40 focus:outline-none transition-colors tracking-[0.3em] text-center text-lg" />
            </div>
          </div>
          <button disabled={!inputId.trim()||inputOtp.length<6}
            className="btn-gradient w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold text-white disabled:opacity-30 disabled:cursor-not-allowed">
            <UserPlus size={18}/>Link Partner <ArrowRight size={16}/>
          </button>
        </div>
      )}

      <Toast message={toast.message} onDismiss={toast.dismiss} />
    </div>
  );
}
